import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Webcam from "react-webcam";
import api, { clearSession, getProfile } from "../api";

const s = {
  page: { display: "flex", minHeight: "100vh" },
  sidebar: { width: 220, background: "#111827", color: "#fff", padding: 24 },
  navItem: (active) => ({ padding: "10px 12px", borderRadius: 6, marginBottom: 6, cursor: "pointer", background: active ? "#2563eb" : "transparent" }),
  main: { flex: 1, padding: 32, overflowY: "auto" },
  card: { background: "#fff", borderRadius: 10, padding: 20, marginBottom: 20, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" },
  input: { padding: 8, marginRight: 8, marginBottom: 8, border: "1px solid #ccc", borderRadius: 6 },
  button: { padding: "8px 14px", background: "#2563eb", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600 },
  table: { width: "100%", borderCollapse: "collapse" },
  th: { textAlign: "left", padding: 8, borderBottom: "2px solid #eee", fontSize: 13, color: "#666" },
  td: { padding: 8, borderBottom: "1px solid #f0f0f0", fontSize: 14 },
  badge: (color) => ({ padding: "2px 8px", borderRadius: 12, fontSize: 12, background: color + "22", color }),
};

export default function LabourDashboard() {
  const [tab, setTab] = useState("checkin");
  const navigate = useNavigate();
  const profile = getProfile();

  const logout = () => { clearSession(); navigate("/login"); };

  return (
    <div style={s.page} className="app-page">
      <div style={s.sidebar} className="app-sidebar">
        <h3 style={{ marginTop: 0 }}>{profile?.name || "Labour"}</h3>
        {["checkin", "attendance", "manual", "payments"].map((t) => (
          <div key={t} style={s.navItem(tab === t)} onClick={() => setTab(t)}>
            {({ checkin: "Check In / Out", attendance: "My Attendance", manual: "Manual Entry", payments: "My Payments" })[t]}
          </div>
        ))}
        <div style={{ ...s.navItem(false), marginTop: 30, color: "#f87171" }} onClick={logout}>Log out</div>
      </div>
      <div style={s.main} className="app-main">
        {tab === "checkin" && <CheckInOutTab />}
        {tab === "attendance" && <AttendanceTab />}
        {tab === "manual" && <ManualEntryTab />}
        {tab === "payments" && <PaymentsTab />}
      </div>
    </div>
  );
}

// ---------------------------------------------------------- Check In/Out --

function CheckInOutTab() {
  const webcamRef = useRef(null);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const getPosition = () =>
    new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true });
    });

  const captureAndSend = async (endpoint) => {
    setBusy(true);
    setMessage("");
    try {
      const shot = webcamRef.current.getScreenshot(); // base64 data URL
      const position = await getPosition();

      const blob = await (await fetch(shot)).blob();
      const fd = new FormData();
      fd.append("face_image", blob, "face.jpg");
      fd.append("latitude", position.coords.latitude);
      fd.append("longitude", position.coords.longitude);

      const res = await api.post(`/api/labour/${endpoint}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMessage(`✓ ${endpoint === "checkin" ? "Checked in" : "Checked out"} at ${res.data.location_name || ""} — ${new Date().toLocaleTimeString()}`);
    } catch (err) {
      setMessage(`✗ ${err.response?.data?.error || "Failed"}`);
    }
    setBusy(false);
  };

  return (
    <div>
      <h2>Check In / Out</h2>
      <div style={s.card}>
        <p style={{ fontSize: 13, color: "#666" }}>
          Look at the camera, then tap Check In or Check Out. You must be physically within range of your assigned location.
        </p>
        <Webcam
          audio={false}
          ref={webcamRef}
          screenshotFormat="image/jpeg"
          width={360}
          videoConstraints={{ facingMode: "user" }}
        />
        <br /><br />
        <button style={{ ...s.button, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={() => captureAndSend("checkin")}>
  {busy ? "Processing..." : "Check In"}
</button>{" "}
<button style={{ ...s.button, background: "#6b7280", opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={() => captureAndSend("checkout")}>
  {busy ? "Processing..." : "Check Out"}
</button>
        {message && <p style={{ marginTop: 16, fontWeight: 600 }}>{message}</p>}
      </div>
    </div>
  );
}

// ----------------------------------------------------------- Attendance ---

function AttendanceTab() {
  const [data, setData] = useState({ records: [], total_approved_hours: 0 });

  useEffect(() => { api.get("/api/labour/attendance").then((r) => setData(r.data)); }, []);

  const statusColor = { approved: "#16a34a", pending: "#d97706", rejected: "#dc2626" };

  return (
    <div>
      <h2>My Attendance</h2>
      <div style={s.card}>
        <strong>Total approved hours: {data.total_approved_hours}</strong>
      </div>
      <div style={s.card}>
        <table style={s.table}>
          <thead>
            <tr><th style={s.th}>Date</th><th style={s.th}>Location</th><th style={s.th}>In</th><th style={s.th}>Out</th><th style={s.th}>Hours</th><th style={s.th}>Source</th><th style={s.th}>Status</th></tr>
          </thead>
          <tbody>
            {data.records.map((r) => (
              <tr key={r.id}>
                <td style={s.td}>{r.attendance_date}</td>
                <td style={s.td}>{r.location_name}</td>
                <td style={s.td}>{new Date(r.intime).toLocaleTimeString()}</td>
                <td style={s.td}>{r.outtime ? new Date(r.outtime).toLocaleTimeString() : "—"}</td>
                <td style={s.td}>{r.hours_worked}</td>
                <td style={s.td}>{r.source}</td>
                <td style={s.td}><span style={s.badge(statusColor[r.status])}>{r.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// -------------------------------------------------------- Manual Entry ---

function ManualEntryTab() {
  const [locations, setLocations] = useState([]);
  const [form, setForm] = useState({ location_id: "", attendance_date: "", intime: "", outtime: "", notes: "" });
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/api/labour/locations").then((r) => setLocations(r.data)); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return; 
    setMessage("");
    try {
      await api.post("/api/labour/attendance/manual", {
        ...form,
        location_id: Number(form.location_id),
        intime: `${form.attendance_date}T${form.intime}:00`,
        outtime: `${form.attendance_date}T${form.outtime}:00`,
      });
      setMessage("Submitted — waiting for admin approval.");
      setForm({ location_id: "", attendance_date: "", intime: "", outtime: "", notes: "" });
    } catch (err) {
      setMessage(err.response?.data?.error || "Failed to submit");
    }finally {
      setBusy(false);         
    }
  };

  return (
    <div>
      <h2>Manual Attendance Entry</h2>
      <div style={s.card}>
        <p style={{ fontSize: 13, color: "#666" }}>Use this if you forgot to check in/out. Your admin must approve it before it counts toward pay.</p>
        {message && <p style={{ fontWeight: 600 }}>{message}</p>}
        <form onSubmit={submit}>
          <select style={s.input} value={form.location_id} onChange={(e) => setForm({ ...form, location_id: e.target.value })} required>
            <option value="">Select location</option>
            {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
          <input style={s.input} type="date" value={form.attendance_date} onChange={(e) => setForm({ ...form, attendance_date: e.target.value })} required />
          <br />
          <input style={s.input} type="time" value={form.intime} onChange={(e) => setForm({ ...form, intime: e.target.value })} required />
          <input style={s.input} type="time" value={form.outtime} onChange={(e) => setForm({ ...form, outtime: e.target.value })} required />
          <br />
          <input style={{ ...s.input, width: 300 }} placeholder="Notes (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          <br />
          <button style={{ ...s.button, opacity: busy ? 0.6 : 1 }} type="submit" disabled={busy}>
  {busy ? "Submitting..." : "submit for approval"}
</button>
        </form>
      </div>
    </div>
  );
}

// ------------------------------------------------------------- Payments ---

function PaymentsTab() {
  const [data, setData] = useState({ payments: [], total_all_time: 0 });

  useEffect(() => { api.get("/api/labour/payments").then((r) => setData(r.data)); }, []);

  return (
    <div>
      <h2>My Payments</h2>
      <div style={s.card}>
        <strong>Total earned (all time): {data.total_all_time}</strong>
      </div>
      <div style={s.card}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Month</th><th style={s.th}>Location</th><th style={s.th}>Regular hrs</th>
              <th style={s.th}>OT hrs</th><th style={s.th}>Total amount</th>
            </tr>
          </thead>
          <tbody>
            {data.payments.map((p) => (
              <tr key={p.id}>
                <td style={s.td}>{p.period_month}</td>
                <td style={s.td}>{p.location_name}</td>
                <td style={s.td}>{p.regular_hours}</td>
                <td style={s.td}>{p.ot_hours}</td>
                <td style={s.td}><strong>{p.total_amount}</strong></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
